import React from "react";
import { Button } from "@/components/ui/button";
import {
  PhoneCall, CheckCircle2, ArrowRightLeft, PlayCircle, Repeat, UserX, Ban,
} from "lucide-react";

const STATUS_LABEL = {
  called: "Appelé",
  serving: "En cours de traitement",
  waiting: "En attente",
};

export function ActiveTicketPanel({
  ticket, counterId, counterName,
  canCancel, hasAllowedCats,
  onCallNext, onRecall, onStart, onComplete, onMissed, onTransfer, onCancel,
  busy,
}) {
  if (!ticket) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="no-active-ticket">
        <div className="text-slate-300 text-8xl font-display font-black mb-4">—</div>
        <p className="text-slate-500 mb-6 text-lg">Aucun ticket actif. Cliquez sur <strong>Appeler prochain</strong> pour commencer.</p>
        <Button
          size="lg" onClick={onCallNext} disabled={!counterId || busy}
          data-testid="call-next-button"
          className="h-16 px-12 text-lg bg-[#DC2626] hover:bg-[#B91C1C]"
        >
          <PhoneCall className="w-5 h-5 mr-2" /> Appeler prochain
        </Button>
        {!counterId && (
          <div className="mt-4 text-xs text-amber-600">Sélectionnez un guichet pour activer le bouton.</div>
        )}
        {counterId && !hasAllowedCats && (
          <div className="mt-4 text-xs text-slate-400">
            Aucune catégorie n'est restreinte sur ce guichet — toutes les catégories peuvent être appelées.
          </div>
        )}
      </div>
    );
  }

  const isCalled = ticket.status === "called";
  const isServing = ticket.status === "serving";

  return (
    <div className="flex-1 flex flex-col">
      <div className="text-center my-6" data-testid="active-ticket-block">
        <div className="text-xs uppercase tracking-[0.4em] text-slate-400">Ticket en cours · {counterName}</div>
        <div className="font-display font-black text-[#DC2626] text-[120px] lg:text-[160px] tracking-tighter mt-2 leading-none"
             data-testid="active-ticket-code">
          {ticket.full_ticket_code}
        </div>
        <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold"
               style={{ background: `${ticket.color}1A`, color: ticket.color }}>
            {ticket.category_label}
          </div>
          <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700"
               data-testid="active-ticket-status">
            {STATUS_LABEL[ticket.status] || ticket.status}
          </div>
          {(ticket.recall_count || 0) > 0 && (
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
              Rappels · {ticket.recall_count}
            </div>
          )}
        </div>
      </div>

      {/* Action grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-auto">
        <Button onClick={onCallNext} disabled={!counterId || busy}
                className="h-16 text-base bg-[#0F172A] hover:bg-slate-800"
                data-testid="action-call-next-active">
          <PhoneCall className="w-5 h-5 mr-2" /> Appeler prochain
        </Button>

        <Button onClick={onRecall} variant="outline" disabled={busy} className="h-16 text-base"
                data-testid="action-recall">
          <Repeat className="w-5 h-5 mr-2" /> Rappeler
        </Button>

        {isCalled && (
          <Button onClick={onStart} disabled={busy}
                  className="h-16 text-base bg-[#0EA5E9] hover:bg-[#0284C7]"
                  data-testid="action-start">
            <PlayCircle className="w-5 h-5 mr-2" /> Démarrer traitement
          </Button>
        )}

        <Button onClick={onComplete} disabled={!isServing && !isCalled || busy}
                className="h-16 text-base bg-[#16A34A] hover:bg-[#15803D]"
                data-testid="action-complete">
          <CheckCircle2 className="w-5 h-5 mr-2" /> Terminer
        </Button>

        <Button onClick={onMissed} variant="outline" disabled={busy}
                className="h-16 text-base text-amber-700 border-amber-400 hover:bg-amber-50"
                data-testid="action-missed">
          <UserX className="w-5 h-5 mr-2" /> Absent
        </Button>

        <Button onClick={onTransfer} variant="outline" disabled={busy}
                className="h-16 text-base"
                data-testid="action-transfer">
          <ArrowRightLeft className="w-5 h-5 mr-2" /> Transférer
        </Button>

        {canCancel && (
          <Button onClick={onCancel} variant="outline" disabled={busy}
                  className="h-16 text-base text-[#DC2626] border-[#DC2626]/40 hover:bg-red-50 col-span-2 md:col-span-3"
                  data-testid="action-cancel">
            <Ban className="w-5 h-5 mr-2" /> Annuler avec motif
          </Button>
        )}
      </div>
    </div>
  );
}
